'use client';

import { useCallback, useState } from 'react';
import Link from 'next/link';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Copy,
  Key,
  KeyRound,
  Loader2,
  Plus,
  Terminal,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { loggedToast } from '@/lib/logged-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ---------------------------------------------------------------------------
// Types — match backend `/public/api` response shape
// ---------------------------------------------------------------------------

interface ApiKeyRecord {
  id: string;
  sub: string;
  name: string;
  key_prefix: string;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  is_active: boolean;
}

interface CreateApiKeyResponse extends ApiKeyRecord {
  api_key: string;
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function fetchApiKeys(): Promise<ApiKeyRecord[]> {
  const res = await fetch('/api/account/api');
  if (!res.ok) throw new Error('Failed to load API keys');
  const data = await res.json();
  return data.items ?? [];
}

async function createApiKey(body: {
  name: string;
  expiresInDays?: number;
}): Promise<CreateApiKeyResponse> {
  const res = await fetch('/api/account/api', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create key');
  }
  return res.json();
}

async function revokeApiKey(id: string): Promise<void> {
  const res = await fetch(`/api/account/api/${id}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to revoke key');
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function keyStatus(key: ApiKeyRecord): {
  label: string;
  variant: 'primary' | 'destructive' | 'secondary';
} {
  if (key.revoked_at) return { label: 'Revoked', variant: 'destructive' };
  if (!key.is_active) return { label: 'Expired', variant: 'secondary' };
  return { label: 'Active', variant: 'primary' };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [value]);

  return (
    <Button variant="outline" size="sm" onClick={copy} className="gap-1.5">
      {copied ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function CreateKeyDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState<string>('90');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createApiKey,
    onSuccess: (data) => {
      setCreatedKey(data.api_key);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key created');
    },
    onError: (err: Error) => {
      loggedToast.error(err.message);
    },
  });

  const handleCreate = () => {
    if (!name.trim()) return;
    mutation.mutate({
      name: name.trim(),
      expiresInDays: expiry === 'never' ? undefined : Number(expiry),
    });
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setName('');
      setExpiry('90');
      setCreatedKey(null);
      mutation.reset();
    }, 200);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => (v ? setOpen(true) : handleClose())}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          Create API Key
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        {!createdKey ? (
          <>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                The key will be shown once. Store it in a safe place.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g. My CRM Integration"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className="grid gap-2">
                <Label>Expires in</Label>
                <Select value={expiry} onValueChange={setExpiry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                    <SelectItem value="never">Never</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!name.trim() || mutation.isPending}
              >
                {mutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                )}
                Create
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>API Key Created</DialogTitle>
              <DialogDescription>
                Copy your key now. You will not be able to see it again.
              </DialogDescription>
            </DialogHeader>
            <div className="my-4">
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md bg-muted px-3 py-2 text-xs font-mono break-all select-all">
                  {createdKey}
                </code>
                <CopyButton value={createdKey} />
              </div>
              <div className="flex items-start gap-2 mt-3 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  This key grants API access on your behalf. Keep it secret.
                </span>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApiKeyRow({ apiKey }: { apiKey: ApiKeyRecord }) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const status = keyStatus(apiKey);

  const revokeMutation = useMutation({
    mutationFn: () => revokeApiKey(apiKey.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key revoked');
    },
    onError: (err: Error) => loggedToast.error(err.message),
  });

  return (
    <>
      <div className="group flex items-center justify-between gap-4 py-3.5 px-5 border-b border-border last:border-b-0 transition-colors hover:bg-muted/40">
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">{apiKey.name}</span>
            <Badge variant={status.variant} size="sm">
              {status.label}
            </Badge>
          </div>
          <code
            className="inline-flex items-center self-start rounded-sm bg-muted/60 px-1.5 py-0.5 text-xs text-muted-foreground font-mono"
            title="Only a prefix of the key is shown. The full key was revealed once at creation and cannot be retrieved again."
          >
            {apiKey.key_prefix}…
          </code>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
            <span>Created {formatDate(apiKey.created_at)}</span>
            {apiKey.expires_at ? (
              <span>Expires {formatDate(apiKey.expires_at)}</span>
            ) : (
              <span>Never expires</span>
            )}
          </div>
        </div>
        {!apiKey.revoked_at && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive shrink-0"
            onClick={() => setConfirmOpen(true)}
            disabled={revokeMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
            <AlertDialogDescription>
              Any application using &quot;{apiKey.name}&quot; will immediately
              lose access. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revokeMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main content
// ---------------------------------------------------------------------------

export function AccountApiKeysContent() {
  const {
    data: keys,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['api-keys'],
    queryFn: fetchApiKeys,
  });

  const activeKeys = keys?.filter((k) => !k.revoked_at) ?? [];
  const revokedKeys = keys?.filter((k) => k.revoked_at) ?? [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] border border-border mt-5 lg:mt-7.5">
      {/* ── Left column ──────────────────────────────────────────── */}
      <div className="flex flex-col [&>*]:border-b [&>*]:border-border [&>*:last-child]:border-b-0">
        {/* Header */}
        <Card variant="grid">
          <CardHeader className="py-5">
            <div className="flex items-center justify-between w-full gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <KeyRound className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold tracking-tight">
                    API Keys
                  </h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Create keys to access the FirmenData API via{' '}
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">
                      api.firmendata.com
                    </code>
                  </p>
                </div>
              </div>
              <CreateKeyDialog />
            </div>
          </CardHeader>
        </Card>

        {/* Active keys */}
        <Card variant="grid">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span>Active Keys</span>
              {activeKeys.length > 0 && (
                <span className="text-xs font-normal text-muted-foreground tabular-nums rounded-full bg-muted px-2 py-0.5">
                  {activeKeys.length}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading && (
              <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading...
              </div>
            )}
            {isError && (
              <div className="py-10 text-center text-sm text-destructive">
                Failed to load API keys
              </div>
            )}
            {!isLoading && !isError && activeKeys.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 py-12 px-5 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                  <Key className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">No active API keys</p>
                <p className="text-xs text-muted-foreground max-w-xs">
                  Create your first key to start making requests to the
                  FirmenData API.
                </p>
              </div>
            )}
            {activeKeys.map((key) => (
              <ApiKeyRow key={key.id} apiKey={key} />
            ))}
          </CardContent>
        </Card>

        {/* Revoked keys */}
        {revokedKeys.length > 0 && (
          <Card variant="grid">
            <CardHeader>
              <CardTitle className="text-muted-foreground">
                Revoked Keys
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 opacity-60">
              {revokedKeys.map((key) => (
                <ApiKeyRow key={key.id} apiKey={key} />
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Right column (sidebar) ───────────────────────────────── */}
      <div className="flex flex-col lg:border-l lg:border-border [&>*]:border-b [&>*]:border-border [&>*:last-child]:border-b-0">
        {/* Quick start */}
        <Card variant="grid">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-muted-foreground" />
              Quick Start
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pt-0 pb-6 space-y-5 text-sm">
            <ol className="space-y-4">
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  1
                </span>
                <div className="flex-1 pt-0.5 min-w-0">
                  <p className="font-medium">Create a key</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    The full key is shown once — copy it immediately.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  2
                </span>
                <div className="flex-1 pt-0.5 min-w-0">
                  <p className="font-medium">Test it &amp; read the docs</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Try your key against live endpoints and browse the full
                    reference in the API Explorer.
                  </p>
                  <Link
                    href="/developer/apis"
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
                  >
                    Open API Explorer
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
